import type { TrackedTranslation } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { Label } from '@ps/web-shared-ui';
import classNames from 'clsx';
import type { FieldSubscription } from 'final-form';
import { isUndefined } from 'lodash';
import type React from 'react';
import { Field } from 'react-final-form';

import Translation from '../../Translation/Translation';
import type { InjectedValidatorProps } from '../../hoc/withValidator';
import withValidator from '../../hoc/withValidator';
import { usePauseValidator } from '../../hooks/usePauseValidator/usePauseValidator';
import useTrackingLabel from '../../hooks/useTrackingLabel/useTrackingLabel';
import DropDown from '../DropDown/DropDown';
import type { Props as DropDownProps, SelectOption } from '../DropDown/DropDown';
import RawInput from '../RawInput/RawInput';

const { error, captionExplanation } = RawInput.styles;

export interface OwnProps {
  id: string;
  label: TrackedTranslation | string;
  hideLabel?: boolean;
  values?: Record<string, string | number>;
  name?: string;
  subscription?: FieldSubscription;
  caption?: React.ReactNode;
  labelClassName?: string;
  labelContent?: React.ReactNode;
  defaultOption?: SelectOption;
  icon?: React.ReactNode;
}
export type Props = DropDownProps & InjectedValidatorProps & OwnProps;

const StarsDropDown: React.FC<Props> = ({
  id,
  name,
  caption,
  onSelect,
  hideLabel,
  validator,
  values,
  validationErrors,
  label,
  defaultOption,
  labelClassName,
  labelContent,
  icon,
  disabled,
  ...rest
}) => {
  const { inputFilled, inputFocused } = useTrackers();
  const fieldName = useTrackingLabel(label, values);
  usePauseValidator();

  return (
    <Field id={id} name={name || id} validate={validator} initialValue={defaultOption?.value}>
      {({ input, meta }) => (
        <>
          <Label
            id={id}
            hidden={hideLabel}
            className={classNames(labelClassName, {
              [error]: (meta.touched || meta.dirty) && meta.error
            })}
          >
            {typeof label === 'string' ? label : <Translation {...label} values={values} />}
            {labelContent}
          </Label>
          <DropDown
            id={id}
            meta={meta}
            name={name || id}
            value={isUndefined(input.value) ? defaultOption?.value : input.value}
            onFocus={() => inputFocused({ fieldName, fieldType: 'dropdown' })}
            onSelect={(option) => {
              const value = option?.value;
              input.onChange(value);
              !disabled &&
                inputFilled({
                  fieldName,
                  strInput: isUndefined(value) ? value : value.toString(),
                  fieldType: 'dropdown',
                  isAutoFilled: false
                });
              onSelect?.(option);
            }}
            icon={icon}
            disabled={disabled}
            {...rest}
          />
          {caption && <div className={captionExplanation}>{caption}</div>}
          {(meta.touched || meta.dirty) && meta.error && meta.error.message && (
            <p className={error}>
              <Translation {...meta.error.message} />
            </p>
          )}
        </>
      )}
    </Field>
  );
};

export default withValidator(StarsDropDown);
